import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { 
  FiTarget, FiShield, FiZap, FiMap, 
  FiActivity, FiAward, FiCpu, FiClock,
  FiCrosshair, FiLayers
} from 'react-icons/fi';
import { FaGamepad, FaSkull } from 'react-icons/fa';
import MicrosoftStoreBtn from '../components/MicrosoftStoreBtn';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-orange/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-orange/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-orange" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const ChronoArena = () => {
  const PRODUCT = getProduct('chrono-arena');
  const STORE_LINK = PRODUCT.storeLink;
  const PRICE = PRODUCT.price;

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-orange/10 border border-accent-orange/20 text-accent-orange font-mono text-xs uppercase tracking-[0.16em] mb-6">
              <SafeIcon icon={FiCpu} /> Retro Boomer-Shooter
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tighter uppercase italic">
              Chrono Arena<br />
              <span className="text-accent-orange">Myth Wars</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed font-light italic">
              "Four mythic ages. One collapsing labyrinth. Slay the god-beasts before all time is devoured."
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <MicrosoftStoreBtn 
                accentColor="bg-accent-orange" 
                className="px-10 py-4 scale-110" 
                href={STORE_LINK}
              />
              <div className="flex flex-col items-start text-left">
                <span className="text-white font-bold">{PRICE}</span>
                <span className="text-xs text-muted">Lifetime License • Windows 10/11</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-12 border-y border-border bg-card/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-primary leading-relaxed">
            A fast-paced first-person shooter that combines classic boomer-shooter action with mythological settings and time-bending mechanics. Cut through Egypt, Greece, Niflheim and Yomi — wield 10 unique weapons from different mythic eras and harness the power of Chrono Surge.
          </p>
        </div>
      </section>

      {/* Reel — the frames for this product, so a visitor sees what it does. */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Chrono Arena</p>
          <Reel frames={PRODUCT.frames} label="Chrono Arena" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Mechanics Grid */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4 uppercase tracking-tight">Game Features</h2>
            <p className="text-muted">Classic boomer-shooter action meets mythic warfare.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={FiMap} 
              title="4 Mythic Ages" 
              desc="Battle through Egypt, Greece, Niflheim and Yomi. Each age has unique enemies, environments, and a god-beast boss." 
            />
            <FeatureCard 
              icon={FiCrosshair} 
              title="10 Unique Weapons" 
              desc="From the Desert Eagle to the Solar Bow and Hellfire Launcher. Each weapon has a devastating Special Attack." 
            />
            <FeatureCard 
              icon={FiClock} 
              title="Chrono Surge" 
              desc="Slow down time to gain the tactical advantage. Master the flow of battle with this time-bending mechanic." 
            />
            <FeatureCard 
              icon={FaGamepad} 
              title="Multiple Modes" 
              desc="Campaign, Horde Mode with roguelite progression, and New Game+ with relics and increased difficulty." 
            />
          </div>
        </div>
      </section>

      {/* Game Modes */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl font-black text-white uppercase italic">Built for <span className="text-accent-orange">The Arena</span></h2>
              <p className="text-muted leading-relaxed">
                Experience a retro boomer-shooter aesthetic with spectacular VFX. Optimized for high-fidelity visuals and immersive audio that brings each mythic age to life. Fully remappable controls and native controller support.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-card border border-border rounded-md">
                  <h4 className="text-white font-bold mb-1">3 Game Modes</h4>
                  <p className="text-xs text-muted">Campaign, Horde Mode, and New Game+ with relics.</p>
                </div>
                <div className="p-4 bg-card border border-border rounded-md">
                  <h4 className="text-white font-bold mb-1">Fully Remappable</h4>
                  <p className="text-xs text-muted">All keys can be rebound in the Options menu.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-none p-8">
              <h3 className="text-xl font-bold text-white mb-6 uppercase">Technical Specs</h3>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">OS</span>
                  <span className="text-white">Windows 10 / 11 (64-bit)</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">Graphics</span>
                  <span className="text-white">OpenGL 3.3 Compatible</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">Controller</span>
                  <span className="text-white">Xbox / XInput Supported</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">Engine</span>
                  <span className="text-white">Godot 4.6</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">Storage</span>
                  <span className="text-white">500 MB - 1 GB</span>
                </div>
              </div>
              <div className="mt-8">
                <MicrosoftStoreBtn 
                  accentColor="bg-accent-orange" 
                  className="w-full" 
                  href={STORE_LINK}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-accent-orange/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <SafeIcon icon={FaSkull} className="w-16 h-16 text-accent-orange mx-auto mb-8 animate-pulse" />
          <h2 className="text-5xl font-black text-white mb-6 uppercase italic">Will you survive the arena?</h2>
          <p className="text-muted mb-10 text-lg">
            {PRODUCT.priceNote}. No microtransactions. No online requirement. Pure mythic action.
          </p>
          <MicrosoftStoreBtn 
            accentColor="bg-accent-orange" 
            className="mx-auto px-16 py-6 scale-125" 
            href={STORE_LINK}
          />
        </div>
      </section>
    </div>
  );
};

export default ChronoArena;
